import React from 'react';
import { AlertCircle, ChevronDown, ExternalLink, FileInput, RefreshCw } from 'lucide-react';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { AppConfirmDialog } from '../../../components/AppConfirmDialog';
import { AppSelect } from '../../../components/AppSelect';
import { projectDetailPath, projectDocumentEditorPath } from '../../../utils/projectRoutes';
import type { AusschreibungDetail, TenderDocument } from '../types';
import { isTenderGaebDocument } from '../types';

type ImportResponse = {
  ok?: boolean;
  inserted?: number;
  error?: string;
  code?: string;
  position_count?: number;
  lead_id?: string;
  offering_id?: string;
};

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

interface Props {
  ausschreibung: AusschreibungDetail;
  documents: TenderDocument[];
  /** Nach LV-Import: Akte neu laden (offering_id, lv_summary). */
  onChanged: () => void | Promise<void>;
  canEditProjects: boolean;
  /** LV aus einem PDF der Akte lesen (Regel-Parser + Seitenspeicher); nur wenn ein PDF da ist. */
  onImportPdf?: () => void | Promise<void>;
  importingPdf?: boolean;
  hasPdfDoc?: boolean;
}

/**
 * LV-Karte der Akte: Stand des Leistungsverzeichnisses im Vorgang, Import aus der GAEB-Datei,
 * Sprung in den Angebotseditor und X84-Export (Angebotsabgabe).
 */
export const TenderLvPanel: React.FC<Props> = ({
  ausschreibung,
  documents,
  onChanged,
  canEditProjects,
  onImportPdf,
  importingPdf = false,
  hasPdfDoc = false,
}) => {
  const [importing, setImporting] = React.useState(false);
  const [open, setOpen] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [replaceCount, setReplaceCount] = React.useState<number | null>(null);

  const gaebDocs = React.useMemo(
    () => documents.filter((d) => d.doc_kind === 'gaeb' || isTenderGaebDocument(d.filename, d.mime_type)),
    [documents],
  );
  const [selectedGaebId, setSelectedGaebId] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (gaebDocs.length === 0) {
      setSelectedGaebId(null);
      return;
    }
    const preferred = ausschreibung.gaeb_document_id && gaebDocs.find((d) => d.id === ausschreibung.gaeb_document_id);
    setSelectedGaebId((prev) => (prev && gaebDocs.some((d) => d.id === prev) ? prev : (preferred ?? gaebDocs[0]).id));
  }, [gaebDocs, ausschreibung.gaeb_document_id]);

  const summary = ausschreibung.lv_summary ?? null;
  const hasLv = !!ausschreibung.offering_id && !!summary;
  const leadId = summary?.lead_id ?? ausschreibung.lead_id ?? null;
  const offeringId = ausschreibung.offering_id ?? null;

  const editorHref = leadId && offeringId ? projectDocumentEditorPath({ id: leadId }, 'offer', offeringId) : null;
  const projectHref = leadId ? projectDetailPath({ id: leadId }) : null;

  const runImport = async (replace: boolean) => {
    if (!selectedGaebId) return;
    setImporting(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(
        `/api/ausschreibungen/${encodeURIComponent(ausschreibung.id)}/import-lv${replace ? '?replace=1' : ''}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ document_id: selectedGaebId }),
        },
      );
      const body = (await res.json().catch(() => ({}))) as ImportResponse;
      if (res.status === 409 && body.code === 'positions_exist') {
        setReplaceCount(body.position_count ?? 0);
        return;
      }
      if (!res.ok || !body.ok) {
        throw new Error(body.error || 'LV-Import fehlgeschlagen.');
      }
      setNotice(`${body.inserted ?? 0} Zeilen aus dem LV in den Vorgang übernommen.`);
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <section className="rounded-lg border border-app-border bg-app-surface shadow-sm">
      <div className={`flex items-center justify-between gap-3 px-4 py-3 ${open ? 'border-b border-app-border' : ''}`}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 min-w-0 text-left"
          aria-expanded={open}
        >
          <ChevronDown className={`w-4 h-4 text-app-text-subtle shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
          <h2 className="text-base font-semibold text-app-text truncate">Leistungsverzeichnis &amp; Kalkulation</h2>
        </button>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {gaebDocs.length > 1 && (
            <div className="w-56 max-w-full">
              <AppSelect
                value={selectedGaebId ?? ''}
                onChange={(v) => setSelectedGaebId(v || null)}
                options={gaebDocs.map((d) => ({ value: d.id, label: d.filename }))}
                aria-label="GAEB-Datei wählen"
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => void runImport(false)}
            disabled={importing || !selectedGaebId || !canEditProjects}
            title={
              !canEditProjects
                ? 'Für den LV-Import wird das Recht „Projekte bearbeiten“ benötigt.'
                : !selectedGaebId
                  ? 'Zuerst eine GAEB-Datei (X83) hochladen.'
                  : hasLv
                    ? 'LV erneut aus der GAEB-Datei lesen (ersetzt die Positionen des Vorgangs)'
                    : 'LV aus der GAEB-Datei in den Vorgang übernehmen'
            }
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-app-text-muted hover:bg-app-hover hover:text-app-text disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {importing ? <LoadingSpinner size="xs" /> : hasLv ? <RefreshCw className="w-3.5 h-3.5" /> : <FileInput className="w-3.5 h-3.5" />}
            {hasLv ? 'LV neu importieren' : 'LV importieren'}
          </button>
          {onImportPdf && hasPdfDoc && (
            <button
              type="button"
              onClick={() => void onImportPdf()}
              disabled={importingPdf || importing || !canEditProjects}
              title={
                !canEditProjects
                  ? 'Für den LV-Import wird das Recht „Projekte bearbeiten“ benötigt.'
                  : 'Titel, Positionen, Mengen und Einheiten aus dem PDF-Leistungsverzeichnis in den Vorgang übernehmen'
              }
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-app-text-muted hover:bg-app-hover hover:text-app-text disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {importingPdf ? <LoadingSpinner size="xs" /> : <FileInput className="w-3.5 h-3.5" />}
              LV aus PDF lesen
            </button>
          )}
          {projectHref && (
            <a
              href={projectHref}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-app-text-muted hover:bg-app-hover hover:text-app-text transition"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Projekt öffnen
            </a>
          )}
          {hasLv && editorHref && (
            <a
              href={editorHref}
              className="inline-flex items-center gap-1.5 rounded-md bg-pipedrive-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 transition"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Im Angebotseditor kalkulieren
            </a>
          )}
        </div>
      </div>

      <div className={`px-4 py-3 space-y-3 ${open ? '' : 'hidden'}`}>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 inline-flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}
        {notice && !error && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{notice}</div>
        )}

        {hasLv && summary ? (
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div className="rounded-md bg-app-surface-muted px-3 py-2">
              <dt className="text-app-text-subtle">Positionen</dt>
              <dd className="text-base font-semibold text-app-text tabular-nums">{summary.item_count}</dd>
            </div>
            <div className="rounded-md bg-app-surface-muted px-3 py-2">
              <dt className="text-app-text-subtle">Titel</dt>
              <dd className="text-base font-semibold text-app-text tabular-nums">{summary.title_count}</dd>
            </div>
            <div className="rounded-md bg-app-surface-muted px-3 py-2">
              <dt className="text-app-text-subtle">Bepreist</dt>
              <dd
                className={`text-base font-semibold tabular-nums ${
                  summary.item_count > 0 && summary.priced_count < summary.item_count ? 'text-amber-700' : 'text-app-text'
                }`}
              >
                {summary.priced_count} / {summary.item_count}
              </dd>
            </div>
            <div className="rounded-md bg-app-surface-muted px-3 py-2">
              <dt className="text-app-text-subtle">Netto</dt>
              <dd className="text-base font-semibold text-app-text tabular-nums">{euro.format(summary.net_total)}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-app-text-muted">
            Noch kein LV im Vorgang. Lade die GAEB-Datei (X83) der Ausschreibung hoch und importiere sie; dabei werden
            Projekt und Vorgang angelegt.
          </p>
        )}

      </div>

      <AppConfirmDialog
        open={replaceCount != null}
        onClose={() => setReplaceCount(null)}
        title="Positionen ersetzen?"
        description={
          <>
            Der Vorgang enthält bereits <span className="font-medium text-app-text">{replaceCount}</span> Positionen.
            Beim erneuten Import werden sie gelöscht und durch das LV aus der GAEB-Datei ersetzt. Kalkulierte Preise
            gehen dabei verloren.
          </>
        }
        confirmLabel="Ersetzen"
        cancelLabel="Abbrechen"
        destructive
        isLoading={importing}
        onConfirm={() => {
          setReplaceCount(null);
          void runImport(true);
        }}
      />

    </section>
  );
};
